(ns io.daskul.webapp.active-context
  "Pure helpers for resolving the active branch/school context (F-041, SMS-272).

   Dependency-light (no React/re-frame/localStorage) so it stays unit-testable
   on the shared :node-test harness — the same seam pattern as
   `io.daskul.webapp.bootstrap` and `io.daskul.webapp.nav`. Each product's
   `…active-context.events/::initialize` reads localStorage + the JWT, calls
   `resolve-context`, then wires the :db + :localstorage/set effects.")

(defn real-branches
  "The subset of `available-contexts` that name an actual branch (a truthy
   :branch-id). A context without one is the 'All Branches' pseudo-context, so
   it does not count toward the single-branch auto-pin decision."
  [available-contexts]
  (filterv :branch-id available-contexts))

(defn resolve-context
  "Resolve the active-context map to hold, plus whether it must be persisted.

   Inputs:
     stored             — the active-context previously saved to localStorage
                          (map or nil). Takes precedence when present.
     jwt                — {:school-id :branch-id} singular ids decoded from the
                          JWT. A school-admin's :branch-id is nil.
     available-contexts — the branch/school contexts the user can access.

   Rules (SMS-272):
     - Base context = the stored map, else the JWT singular ids.
     - Layer 2 auto-pin: when the base has no :branch-id AND the school has
       exactly one real branch, pin that branch. This covers fresh logins and
       backfills an already-stored nil context (why we may persist even when a
       `stored` map existed).
     - Multi-branch nil stays nil = 'All Branches' (supported, not force-pinned).

   Returns {:active <map> :persist? <bool>}. Persist when nothing was stored
   (writing the defaults) OR the auto-pin changed the context (backfill)."
  [stored jwt available-contexts]
  (let [{:keys [school-id branch-id]} jwt
        base (or stored
                 (cond-> {}
                   school-id (assoc :school-id school-id)
                   branch-id (assoc :branch-id branch-id)))
        branches (real-branches available-contexts)
        auto-pin? (and (nil? (:branch-id base))
                       (= 1 (count branches)))
        active (cond-> base
                 auto-pin? (assoc :branch-id (:branch-id (first branches))))]
    {:active active
     :persist? (or (nil? stored) auto-pin?)}))
